import { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Tag,
  Switch,
  Select,
  Input,
  Space,
  Button,
  Modal,
  Spin,
  message,
  Form,
  Row,
  Col,
  Typography,
  Divider,
  Popconfirm,
  Upload,
  Tooltip,
} from "antd";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import type { ColumnsType } from "antd/es/table";
import Header from "../../components/Header/header";
import * as Actions from "../../store/slices/templateMapping/templateMapping"
import { connect } from "react-redux";
import styles from './templatemapping.module.css';
import { hasPermission } from "../../utils/premission";
import { PERMISSIONS } from "../../utils/constant/permissions";
import { useNavigate } from 'react-router-dom';



interface Api {
  Template_Red: any;
  getAllTemp: (params: { page: number; size: number }) => void;
  CreateTemplate: (payload: any) => void;
  GetTemplateById: (id: number) => unknown;
  DeleteTemplate: (id: number) => unknown;
  UpdateTemplate: (payload: any) => unknown;
  getAllDbField: () => unknown
}

const { Title } = Typography;

interface TemplateMapping {
  id: number;
  csvTemplateId: number;
  csvFieldName: string;
  dbFieldName: string;
  defaultValue?: string;
  fixedValue?: string;
}

interface CsvTemplate {
  id: number;
  templateName: string;
  description: string;
  isActive: boolean;
  templateDetails: TemplateMapping[];
}

// const dummyTemplates: CsvTemplate[] = [
//   {
//     id: 1,
//     templateName: "Customer Import 2025",
//     description: "Standard customer data import from marketing team",
//     isActive: true,
//     templateDetails: [
//       { id: 1, csvTemplateId: 1, csvFieldName: "Full Name", dbFieldName: "name", defaultValue: "", fixedValue: "" },
//       { id: 2, csvTemplateId: 1, csvFieldName: "Email Address", dbFieldName: "email", defaultValue: "", fixedValue: "" },
//       { id: 3, csvTemplateId: 1, csvFieldName: "Phone", dbFieldName: "phone_number", defaultValue: "N/A", fixedValue: "" },
//     ],
//   },
// ];

const TemplateMappingPage: React.FC<Api> = ({ DeleteTemplate, Template_Red, getAllTemp, CreateTemplate, GetTemplateById, UpdateTemplate, getAllDbField }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<CsvTemplate[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CsvTemplate | null>(null);
  const [dbFields, setDbFields] = useState<{ dbFieldName: string; displayFieldName: string }[]>([]);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [newMapping, setNewMapping] = useState<Partial<TemplateMapping>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);




  useEffect(() => {
    getAllDbField();
    fetchAllTemplates();
  }, []);

  const fetchAllTemplates = () => {
    setTableLoading(true);
    const token = localStorage.getItem('loginToken');
    fetch(`${process.env.REACT_APP_API_URL}/csv-template/get-all/0/10000`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(res => {
        const list = Array.isArray(res) ? res : res?.content || [];
        setTemplates(list);
        setTotal(list.length);
      })
      .catch(() => { setTemplates([]); setTotal(0); })
      .finally(() => setTableLoading(false));
  };


  useEffect(() => {
    if (Template_Red?.getdbfield[0]?.res) {
      setDbFields(Template_Red?.getdbfield[0]?.res);
    }
  }, [Template_Red]);
  // Safe for large files (100MB+) — never loads full file into memory
  const MAX_FILE_SIZE_MB = 50;

  const handleSampleFileUpload = (file: File) => {
    // Size warning
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      message.warning(`File is ${sizeMB.toFixed(1)}MB. Only the first row (headers) will be read — this may take a moment.`);
    }

    const isCSV = file.name.endsWith('.csv') || file.type === 'text/csv';

    if (isCSV) {
      // CSV: read only first 4KB — enough to get the header line
      const slice = file.slice(0, 4096);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const firstLine = text.split('\n')[0];
          const columns = firstLine
            .split(',')
            .map((col) => col.trim().replace(/^"|"$/g, ''))
            .filter(Boolean);

          if (columns.length === 0) {
            message.warning('No columns found in the file.');
            return;
          }
          form.setFieldsValue({
            templateDetails: columns.map((col) => ({
              csvFieldName: col,
              dbFieldName: undefined,
              defaultValue: '',
              fixedValue: '',
            })),
          });
          message.success(`${columns.length} columns loaded from CSV.`);
        } catch {
          message.error('Failed to read CSV file.');
        }
      };
      reader.readAsText(slice);
    } else {
      // Excel: read only first 256KB — XLSX header/sheet metadata is at the start
      const slice = file.slice(0, 262144);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          // sheetRows: 1 → parse only first row
          const workbook = XLSX.read(data, { type: 'binary', sheetRows: 1 });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

          const columns = rows.length > 0
            ? (rows[0] as string[]).map((col) => String(col ?? '').trim()).filter(Boolean)
            : [];

          if (columns.length === 0) {
            message.warning('No columns found in the file.');
            return;
          }
          form.setFieldsValue({
            templateDetails: columns.map((col) => ({
              csvFieldName: col,
              dbFieldName: undefined,
              defaultValue: '',
              fixedValue: '',
            })),
          });
          message.success(`${columns.length} columns loaded from Excel.`);
        } catch {
          message.error('Failed to read Excel file. Try uploading a smaller sample file.');
        }
      };
      reader.readAsBinaryString(slice);
    }

    return false; // prevent antd auto-upload
  };


  console.log('Template_Red full object:', Template_Red);
  console.log('templates state:', templates);

  useEffect(() => {
    if (editingTemplate) {
      form.setFieldsValue({
        templateName: editingTemplate.templateName,
        description: editingTemplate.description,
        isActive: editingTemplate.isActive,
      });
    } else {
      form.resetFields();
    }
  }, [editingTemplate, form]);



  useEffect(() => {
    if (editingTemplate) {
      form.setFieldsValue({
        templateName: editingTemplate.templateName,
        description: editingTemplate.description,
        isActive: editingTemplate.isActive,
      });
    } else {
      form.resetFields();
    }
  }, [editingTemplate, form]);






  const columns: ColumnsType<CsvTemplate> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Template Name",
      dataIndex: "templateName",
      key: "templateName",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Mappings",
      key: "mappings",
      render: (_, record) => (
        <Tag color="blue">
          {record.templateDetails?.length ?? 0} fields
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (active: boolean) => (
        <Tag color={active ? "success" : "error"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <Space>
          {hasPermission(PERMISSIONS.CSV_TEMPLATE_UPDATE) && (

            <Button
              size="small"
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
          <Popconfirm
            title="Delete template"
            description="Are you sure you want to delete this mapping template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            {hasPermission(PERMISSIONS.CSV_TEMPLATE_DELETE) && (
              <Button size="small" danger>
                Delete
              </Button>
            )}
          </Popconfirm>
        </Space>
      ),
    },
  ];







  const handleAddNew = () => {
    setEditingTemplate(null);
    form.setFieldsValue({
      templateDetails: [],   // 🔥 REQUIRED
      isActive: true,
    });
    setIsModalOpen(true);
  };



  // const handleEdit = (template: CsvTemplate) => {
  //   setEditingTemplate(template);
  //   setIsModalOpen(true);
  // };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: 'Delete Template',
      content: 'Are you sure you want to delete this template? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      maskClosable: false,
      onOk: async () => {
        setTableLoading(true);

        try {
          await DeleteTemplate(id);

          message.success('Template deleted successfully');

          fetchAllTemplates();

          if (editingTemplate?.id === id) {
            setIsModalOpen(false);
            form.resetFields();
            setEditingTemplate(null);
          }
        } catch (error: any) {
          console.error('Failed to delete template:', error);
          message.error(
            error?.message || 'Failed to delete template. Please try again.'
          );
        } finally {
          setTableLoading(false);
        }
      },
    });
  };





  const handleModalOk = async () => {
    try {
      setModalLoading(true);

      const values = await form.validateFields();

      const templateDetailsClean = (values.templateDetails || []).map(
        ({ csvFieldName, dbFieldName, defaultValue, fixedValue }: any) => ({
          csvFieldName,
          dbFieldName,
          defaultValue: defaultValue || "",
          fixedValue: fixedValue || "",
        })
      );

      const payload = {
        id: editingTemplate ? editingTemplate.id : null,
        templateName: values.templateName,
        description: values.description,
        isActive: values.isActive ?? true,
        templateDetails: templateDetailsClean,
      };

      let res: any;
      if (editingTemplate) {
        res = await UpdateTemplate(payload);
        if (res?.success === false) {
          message.error(res?.message || "Update failed");
          return;
        }
        message.success("Template updated successfully");
      } else {
        res = await CreateTemplate(payload);
        message.success("Template created successfully");
        setCurrentPage(1);
      }

      setIsModalOpen(false);
      form.resetFields();
      setEditingTemplate(null);
      fetchAllTemplates();
    } catch (err: any) {
      message.error(err?.message || "Operation failed");
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = async (template: CsvTemplate) => {
    setModalLoading(true);

    try {
      const res: any = await GetTemplateById(template.id);

      if (res?.success) {
        const data = res.data;

        setEditingTemplate(data);

        form.setFieldsValue({
          templateName: data.templateName,
          description: data.description,
          isActive: data.isActive,
          templateDetails: data.templateDetails || [],
        });

        setIsModalOpen(true);
        message.success("Template loaded successfully");
      } else {
        message.error(res?.message || "Failed to load template");
      }
    } catch (err) {
      message.error("Something went wrong while loading template");
    } finally {
      setModalLoading(false);
    }
  };






  return (
    <>
      <Header />
      <div className={styles.container}>
        <div style={{ marginBottom: 16 }}>
          <Title level={4}>File / Template Mapping</Title>
        </div>

        <Card bordered={false}>
          <Space style={{ marginBottom: 16 }}>
            {hasPermission(PERMISSIONS.CSV_TEMPLATE_CREATE) && (

              <Button type="primary" onClick={handleAddNew}>
                Create New Template
              </Button>
            )}
          </Space>

          <Table
            columns={columns}
            dataSource={templates.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
            rowKey="id"
            loading={tableLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (totalCount, range) =>
                `${range[0]}-${range[1]} of ${totalCount} templates`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
          />
        </Card>

        <Modal
          title={editingTemplate ? "Edit Template" : "Create New Template"}
          open={isModalOpen}
          onOk={handleModalOk}
          confirmLoading={modalLoading}
          onCancel={() => {
            setIsModalOpen(false);
            form.resetFields();
          }}
          okText={editingTemplate ? "Save Changes" : "Create"}
          width={900}
          styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 } }}
        >
          <Spin spinning={modalLoading}>
            <Form form={form} layout="vertical" size="small">
              {/* Top section */}
              <Row gutter={16} align="middle">
                <Col span={16}>
                  <Form.Item
                    name="templateName"
                    label="Template Name"
                    rules={[{ required: true, message: "Please enter template name" }]}
                    style={{ marginBottom: 12 }}
                  >
                    <Input placeholder="e.g. Customer Import 2025" />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item name="description" label="Description" style={{ marginBottom: 12 }}>
                    <Input placeholder="Short description..." />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item name="isActive" label="Active" valuePropName="checked" style={{ marginBottom: 12 }}>
                    <Switch checkedChildren="Yes" unCheckedChildren="No" defaultChecked />
                  </Form.Item>
                </Col>
              </Row>

              <Divider style={{ margin: '8px 0 12px' }} />

              {/* Upload + header row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#1a1f36' }}>Field Mappings</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Upload
                    accept=".csv,.xlsx,.xls"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleSampleFileUpload(file as unknown as File);
                      return false;
                    }}
                  >
                    <Button icon={<UploadOutlined />} size="small" type="primary" ghost>
                      Auto-fill from File
                    </Button>
                  </Upload>
                  <Tooltip title="Upload a CSV or Excel file — column headers will auto-fill the CSV Field Name column below">
                    <InfoCircleOutlined style={{ color: '#8c8fa3' }} />
                  </Tooltip>
                </div>
              </div>

              {/* Column headers */}
              <Row gutter={8} style={{ marginBottom: 4, padding: '0 0 4px', borderBottom: '1px solid #f0f0f0' }}>
                <Col span={6}><span style={{ fontSize: 11, color: '#8c8fa3', fontWeight: 600 }}>CSV FIELD NAME</span></Col>
                <Col span={10}><span style={{ fontSize: 11, color: '#8c8fa3', fontWeight: 600 }}>DB FIELD NAME</span></Col>
                <Col span={4}><span style={{ fontSize: 11, color: '#8c8fa3', fontWeight: 600 }}>DEFAULT VALUE</span></Col>
                <Col span={3}><span style={{ fontSize: 11, color: '#8c8fa3', fontWeight: 600 }}>FIXED VALUE</span></Col>
                <Col span={1}></Col>
              </Row>

              <Form.List name="templateDetails">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <Row gutter={8} key={key} align="middle" style={{ marginBottom: 0 }}>
                        <Col span={6}>
                          <Form.Item
                            name={[name, "csvFieldName"]}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ marginBottom: 8 }}
                          >
                            <Input placeholder="CSV column name" />
                          </Form.Item>
                        </Col>

                        <Col span={10}>
                          <Form.Item
                            name={[name, "dbFieldName"]}
                            rules={[{ required: true, message: "Required" }]}
                            style={{ marginBottom: 8 }}
                          >
                            <Select
                              placeholder="Select DB field"
                              showSearch
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              disabled={modalLoading}
                            >
                              {dbFields.map((field) => (
                                <Select.Option
                                  key={field.dbFieldName}
                                  value={field.dbFieldName}
                                  label={field.displayFieldName}
                                >
                                  {field.displayFieldName}
                                  <span style={{ color: '#aaa', fontSize: 11, marginLeft: 4 }}>({field.dbFieldName})</span>
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col span={4}>
                          <Form.Item name={[name, "defaultValue"]} style={{ marginBottom: 8 }}>
                            <Input placeholder="Default" />
                          </Form.Item>
                        </Col>

                        <Col span={3}>
                          <Form.Item name={[name, "fixedValue"]} style={{ marginBottom: 8 }}>
                            <Input placeholder="Fixed" />
                          </Form.Item>
                        </Col>

                        <Col span={1} style={{ textAlign: 'center' }}>
                          <Button
                            danger
                            type="text"
                            size="small"
                            disabled={modalLoading}
                            onClick={() => remove(name)}
                            style={{ padding: '0 4px', marginBottom: 8 }}
                          >
                            ✕
                          </Button>
                        </Col>
                      </Row>
                    ))}

                    <Button
                      type="dashed"
                      block
                      onClick={() => add()}
                      disabled={modalLoading}
                      style={{ marginTop: 4 }}
                    >
                      + Add Row
                    </Button>
                  </>
                )}
              </Form.List>
            </Form>
          </Spin>

        </Modal>
      </div>
    </>
  );
}

function mapStateToProps(state: any) {
  return {
    Template_Red: state.Template_Red
  }
}

export default connect(mapStateToProps, Actions)(TemplateMappingPage);